<template>
  <div class="animated fadeIn">
    <!-- Botões de ação -->
    <div class="mb-2 d-flex justify-content-between align-items-center">
      <b-button
        v-b-toggle.collapse-filter
        class="btn btn-third btn-sm"
      >
        Filtrar <i class="fas fa-filter"></i>
      </b-button>
      <b-button
        class="btn btn-success btn-sm"
        @click="exportToExcel"
      >
        <i class="fa fa-file-excel-o"></i> Exportar
      </b-button>
    </div>

    <!-- Collapse com Filtros -->
    <b-collapse id="collapse-filter" class="mb-3">
      <div class="card">
        <div class="card-body p-3">
          <div class="row align-items-end">
            <div class="col-md-3 col-sm-6 mb-2">
              <label class="small font-weight-bold mb-1">Filtrar por</label>
              <select v-model="filterType" class="form-control form-control-sm">
                <option value="username">Usuário</option>
                <option value="cpf">CPF</option>
              </select>
            </div>
            <div class="col-md-5 col-sm-6 mb-2">
              <label class="small font-weight-bold mb-1">Valor</label>
              <input
                v-if="filterType === 'cpf'"
                v-model="searchQuery"
                v-mask="'###.###.###-##'"
                type="text"
                class="form-control form-control-sm"
                placeholder="Digite o CPF..."
                @keyup.enter="applySearch"
              >
              <input
                v-else
                v-model="searchQuery"
                type="text"
                class="form-control form-control-sm"
                placeholder="Digite o nome do usuário..."
                @keyup.enter="applySearch"
              >
            </div>
            <div class="col-md-2 col-sm-6 mb-2">
              <button
                type="button"
                class="btn btn-third btn-sm btn-block"
                @click="applySearch"
              >
                <i class="fas fa-search"></i> PESQUISAR
              </button>
            </div>
            <div class="col-md-2 col-sm-6 mb-2" v-if="hasActiveFilter">
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm btn-block btn-clear-custom"
                @click="clearSearch"
              >
                <i class="fas fa-times"></i> LIMPAR
              </button>
            </div>
          </div>
        </div>
      </div>
    </b-collapse>

    <!-- Tabela -->
    <div class="card">
      <div class="card-header bg-light py-2 px-3">
        <div class="d-flex align-items-center">
          <small class="font-weight-bold mr-2">Mostrar</small>
          <select
            v-model.number="pageSize"
            class="form-control form-control-sm"
            style="width: auto;"
            @change="onPageSizeChange"
          >
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
          <small class="font-weight-bold ml-2">registros</small>
        </div>
      </div>
      <div class="card-block table-responsive">
        <table class="table table-hover" style="border: 1px solid #dcdede;">
          <thead class="thead-third" style="border: solid 1px #0277bd; height: 44px">
            <tr>
              <th class="text-center">Usuário</th>
              <th class="text-center">CPF</th>
              <th class="text-center">Operação</th>
              <th class="text-center">Status</th>
              <th class="text-center">Campos Alterados</th>
              <th class="text-center">Erro</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="paginatedResults.length === 0">
              <td colspan="6" class="text-center text-muted py-4">
                <i class="fa fa-search fa-2x mb-2"></i>
                <br>
                {{ hasActiveFilter ? 'Nenhum usuário encontrado' : 'Nenhum usuário processado' }}
              </td>
            </tr>
            <tr v-for="(result, index) in paginatedResults" :key="index" :class="getRowClass(result)">
              <td class="text-center">
                <strong>{{ result.username }}</strong>
              </td>
              <td class="text-center">
                <code class="small">{{ formatCPF(result.cpf) }}</code>
              </td>
              <td class="text-center">
                <b-badge :variant="getOperationVariant(result.operation)" class="px-2">
                  {{ getOperationLabel(result.operation) }}
                </b-badge>
              </td>
              <td class="text-center">
                <b-badge :variant="getUserStatusVariant(result.status)" class="px-2">
                  {{ getUserStatusLabel(result.status) }}
                </b-badge>
              </td>
              <td class="text-center">
                <span v-if="result.changedFields && result.changedFields.length > 0">
                  <b-badge
                    v-for="field in result.changedFields"
                    :key="field"
                    variant="warning"
                    class="mr-1 mb-1 small"
                  >
                    {{ field }}
                  </b-badge>
                </span>
                <span v-else class="text-muted small">-</span>
              </td>
              <td>
                <span v-if="result.errorMessage" class="error-message" :class="{ 'highlight-error': highlightErrors }">
                  <i class="fa fa-exclamation-circle text-danger"></i>
                  {{ result.errorMessage }}
                </span>
                <span v-else class="text-muted small">-</span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Paginação -->
        <div class="d-flex justify-content-between align-items-center mt-2 px-3">
          <small class="text-muted">
            Mostrando <strong>{{ startRecord }}</strong> a <strong>{{ endRecord }}</strong> de <strong>{{ filteredResults.length }}</strong> usuários
            <span v-if="hasActiveFilter" class="ml-2">
              <i class="fa fa-filter text-primary"></i> (filtrado de <strong>{{ results.length }}</strong> total)
            </span>
          </small>

          <nav v-if="totalPages > 1">
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" :class="{ 'disabled': currentPage === 1 }">
                <a class="page-link" href="javascript:void(0);" @click="goToPage(currentPage - 1)">
                  Anterior
                </a>
              </li>
              <li
                v-for="page in visiblePages"
                :key="page"
                class="page-item"
                :class="{ 'active': page === currentPage }"
              >
                <a class="page-link" href="javascript:void(0);" @click="goToPage(page)">
                  {{ page }}
                </a>
              </li>
              <li class="page-item" :class="{ 'disabled': currentPage === totalPages }">
                <a class="page-link" href="javascript:void(0);" @click="goToPage(currentPage + 1)">
                  Próximo
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'

export default {
  name: 'UserResultsTable',
  props: {
    results: {
      type: Array,
      default: () => []
    },
    showAll: {
      type: Boolean,
      default: false
    },
    highlightErrors: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      filterType: 'username',
      searchQuery: '',
      appliedSearchQuery: '',
      appliedFilterType: 'username',
      pageSize: 10,
      currentPage: 1
    }
  },
  computed: {
    hasActiveFilter () {
      return this.appliedSearchQuery !== ''
    },
    filteredResults () {
      if (!this.results) return []
      if (!this.appliedSearchQuery) return this.results

      const query = this.appliedSearchQuery.toLowerCase().replace(/[.-]/g, '')

      if (this.appliedFilterType === 'cpf') {
        return this.results.filter(result =>
          result.cpf && result.cpf.replace(/[.-]/g, '').includes(query)
        )
      } else {
        return this.results.filter(result =>
          result.username && result.username.toLowerCase().includes(query)
        )
      }
    },
    paginatedResults () {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.filteredResults.slice(start, end)
    },
    totalPages () {
      return Math.ceil(this.filteredResults.length / this.pageSize)
    },
    startRecord () {
      if (this.filteredResults.length === 0) return 0
      return (this.currentPage - 1) * this.pageSize + 1
    },
    endRecord () {
      const end = this.currentPage * this.pageSize
      return end > this.filteredResults.length ? this.filteredResults.length : end
    },
    visiblePages () {
      const pages = []
      const maxVisible = 5
      let start = Math.max(1, this.currentPage - 2)
      let end = Math.min(this.totalPages, start + maxVisible - 1)

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1)
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      return pages
    }
  },
  methods: {
    applySearch () {
      this.appliedSearchQuery = this.searchQuery
      this.appliedFilterType = this.filterType
      this.currentPage = 1 // Reset para primeira página ao filtrar
    },
    clearSearch () {
      this.searchQuery = ''
      this.appliedSearchQuery = ''
      this.filterType = 'username'
      this.appliedFilterType = 'username'
      this.currentPage = 1
    },
    onPageSizeChange () {
      this.currentPage = 1 // Reset para primeira página ao mudar tamanho
    },
    goToPage (page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
    },
    async exportToExcel () {
      try {
        // Exportar TODOS os resultados (não apenas os filtrados)
        const dataToExport = this.results.map(r => ({
          'Usuário': r.username || '',
          'CPF': this.formatCPF(r.cpf) || '',
          'Operação': this.getOperationLabel(r.operation) || '',
          'Status': this.getUserStatusLabel(r.status) || '',
          'Campos Alterados': (r.changedFields || []).join(', '),
          'Erro': r.errorMessage || ''
        }))

        const ws = XLSX.utils.json_to_sheet(dataToExport)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Usuários')

        // Ajustar largura das colunas
        const colWidths = [
          { wch: 20 }, // Usuário
          { wch: 15 }, // CPF
          { wch: 12 }, // Operação
          { wch: 12 }, // Status
          { wch: 30 }, // Campos Alterados
          { wch: 50 } // Erro
        ]
        ws['!cols'] = colWidths

        // Gerar arquivo
        XLSX.writeFile(wb, `usuarios-sync-${Date.now()}.xlsx`)
      } catch (error) {
        console.error('Erro ao exportar Excel:', error)
        alert('Erro ao exportar arquivo. Verifique o console.')
      }
    },
    formatCPF (cpf) {
      if (!cpf) return '-'
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    },
    getOperationLabel (operation) {
      const labels = {
        'CREATE': 'Criar',
        'UPDATE': 'Atualizar',
        'SKIP': 'Pular',
        'ERROR': 'Erro',
        'UNCHANGED': 'Sem mudança'
      }
      return labels[operation] || operation
    },
    getOperationVariant (operation) {
      const variants = {
        'CREATE': 'success',
        'UPDATE': 'info',
        'SKIP': 'secondary',
        'ERROR': 'danger',
        'UNCHANGED': 'light'
      }
      return variants[operation] || 'secondary'
    },
    getUserStatusLabel (status) {
      const labels = {
        'COMPLETED': 'Sucesso',
        'FAILED': 'Falhou',
        'SKIPPED': 'Ignorado'
      }
      return labels[status] || status
    },
    getUserStatusVariant (status) {
      const variants = {
        'COMPLETED': 'success',
        'FAILED': 'danger',
        'SKIPPED': 'secondary'
      }
      return variants[status] || 'secondary'
    },
    getRowClass (result) {
      if (result.status === 'FAILED') return 'table-danger'
      if (result.operation === 'CREATE' && result.status === 'COMPLETED') return 'table-success-light'
      if (result.operation === 'UPDATE' && result.status === 'COMPLETED') return 'table-info-light'
      return ''
    }
  }
}
</script>

<style scoped>
.btn-clear-custom {
  background-color: #e9ecef !important;
  border-color: #6c757d !important;
  color: #6c757d !important;
}

.btn-clear-custom:hover {
  background-color: #dee2e6 !important;
  border-color: #6c757d !important;
  color: #6c757d !important;
}

/* Ocultar paginação do Tables quando showPaging=false */
::v-deep .card-block + div {
  display: none;
}

/* Margem para texto de exibição */
::v-deep .card-block > div:last-child {
  margin-left: 0.5rem !important;
  margin-top: 0.5rem !important;
}

.error-message {
  font-size: 0.813rem;
  display: block;
  word-break: break-word;
}

.highlight-error {
  background-color: #fff3cd;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  border-left: 3px solid #dc3545;
}

.table-danger {
  background-color: rgba(220, 53, 69, 0.1) !important;
}

.table-success-light {
  background-color: rgba(40, 167, 69, 0.05) !important;
}

.table-info-light {
  background-color: rgba(23, 162, 184, 0.05) !important;
}

.table tbody tr {
  transition: background-color 0.2s ease;
}

.table tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.08) !important;
}
</style>



// WEBPACK FOOTER //
// src/views/ADSync/components/dashboard/UserResultsTable.vue